'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@core/ui/primitives/tabs';
import { Badge } from '@core/ui/primitives/badge';
import { Spinner } from '@core/ui/primitives/spinner';
import { OrganizationIdentityPanel } from './OrganizationIdentityPanel';
import { OrganizationMembersPanel } from './OrganizationMembersPanel';
import { OrganizationRoadmapPanel } from './OrganizationRoadmapPanel';
import { OrganizationSubscriptionPanel } from './OrganizationSubscriptionPanel';
import { LuTrash2, LuEllipsis, LuSave } from 'react-icons/lu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@core/ui/primitives/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@core/ui/primitives/alert-dialog';
import { Button } from '@core/ui/primitives/button';
import { Input } from '@core/ui/primitives/input';
import { useToast } from '@/core/ui/hooks/use-toast';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/core/ui/primitives/tooltip';

interface OrganizationInfo {
  id: string;
  name: string;
  slug: string;
  role?: string | null;
  privileges?: string[];
}

interface OrganizationSettingsTabsProps {
  organization: OrganizationInfo;
  onOrganizationUpdated?: (organization: { id: string; name: string; slug: string }) => void;
  onOrganizationDeleted?: () => void;
  setHeaderActions: (actions: React.ReactNode) => void;
}

export function OrganizationSettingsTabs({
  organization,
  onOrganizationUpdated,
  onOrganizationDeleted,
  setHeaderActions,
}: OrganizationSettingsTabsProps) {
  const [activeTab, setActiveTab] = useState<'identity' | 'members' | 'subscription' | 'roadmap'>('identity');
  const [tabLoading, setTabLoading] = useState<boolean>(false);
  const loadingStartRef = useRef<number | null>(null);
  const { toast } = useToast();

  // Delete dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Identity Tab State
  const [name, setName] = useState(organization.name);
  const [slug, setSlug] = useState(organization.slug);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(organization.name);
    setSlug(organization.slug);
  }, [organization.name, organization.slug]);

  const privileges = organization.privileges ?? [];
  const canDelete = privileges.includes('org:delete');
  const canManageMembers = privileges.includes('org:members');

  // Form Handling
  const isDirty = name !== organization.name || slug !== organization.slug;
  const slugRegex = /^[a-z0-9-]+$/;
  const isSlugValid = slugRegex.test(slug) && slug.length > 0;
  const isNameValid = name.trim().length > 0;
  const isValid = isSlugValid && isNameValid;

  const handleSave = useCallback(async () => {
    if (!isDirty || !isValid) return;

    setSaving(true);
    try {
      const response = await fetch(`/api/organizations/${organization.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          slug: slug.trim(),
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to update organization');
      }

      const data = await response.json();
      toast({
        title: 'Organization updated',
        description: 'Organization details have been updated successfully.',
        variant: 'success',
      });

      onOrganizationUpdated?.(data.organization);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  }, [isDirty, isValid, name, slug, organization.id, toast, onOrganizationUpdated]);

  // Header Actions
  useEffect(() => {
    if (activeTab !== 'identity') {
      setHeaderActions(null);
      return;
    }

    if (!isDirty) {
      setHeaderActions(null);
      return;
    }

    // Render save button matching VaultMain.tsx pattern exactly
    const saveButton = (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type='button'
            className='inline-flex items-center justify-center h-7 w-7 rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50'
            aria-label='Save changes'
            disabled={!isValid || saving}
            onClick={handleSave}>
            {saving ? <Spinner className='h-3.5 w-3.5' /> : <LuSave className='h-3.5 w-3.5' />}
          </button>
        </TooltipTrigger>
        <TooltipContent side='top'>Save changes</TooltipContent>
      </Tooltip>
    );
    setHeaderActions(saveButton);
  }, [activeTab, isDirty, isValid, saving, handleSave, setHeaderActions]);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const response = await fetch(`/api/organizations/${organization.id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organizationName: deleteConfirmName }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to delete organization');
      }

      toast({
        title: 'Organization deleted',
        description: 'The organization has been permanently deleted.',
        variant: 'success',
      });
      setShowDeleteDialog(false);
      // Fire event so pages can redirect if viewing this organization
      try {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('organization:deleted', { detail: { slug: organization.slug } }));
        }
      } catch {}
      onOrganizationDeleted?.();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const startLoading = () => {
    loadingStartRef.current = Date.now();
    setTabLoading(true);
  };

  const finishLoading = (opts?: { immediate?: boolean }) => {
    const started = loadingStartRef.current ?? Date.now();
    const elapsed = Date.now() - started;
    const min = opts?.immediate ? 0 : 200;
    const delay = Math.max(0, min - elapsed);
    window.setTimeout(() => {
      requestAnimationFrame(() => {
        setTabLoading(false);
        loadingStartRef.current = null;
      });
    }, delay);
  };

  // Initial load - mark loading complete after mount
  useEffect(() => {
    const timer = setTimeout(() => {
      finishLoading({ immediate: true });
    }, 100);
    return () => clearTimeout(timer);
  }, [organization.id]);

  // Reset input when dialog is closed/cancelled
  useEffect(() => {
    if (!showDeleteDialog) {
      setDeleteConfirmName('');
    }
  }, [showDeleteDialog]);

  return (
    <div className='flex h-full min-h-0 flex-col gap-6'>
      <Tabs
        value={activeTab}
        onValueChange={(v) => {
          setActiveTab(v as any);
          startLoading();
          // Tabs will call onReady to finish loading
        }}
        className='flex h-full min-h-0 flex-col'>
        <div className='flex shrink-0 flex-wrap items-center justify-between gap-3'>
          <TabsList>
            <TabsTrigger value='identity'>Identity</TabsTrigger>
            {canManageMembers && <TabsTrigger value='members'>Members</TabsTrigger>}
            <TabsTrigger value='subscription'>Subscription</TabsTrigger>
            <TabsTrigger value='roadmap'>Roadmap</TabsTrigger>
          </TabsList>

          {canDelete && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant='ghost' size='icon'>
                  <LuEllipsis className='h-4 w-4' />
                  <span className='sr-only'>More actions</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <DropdownMenuItem
                  className='text-destructive focus:text-destructive'
                  onSelect={() => setShowDeleteDialog(true)}>
                  <LuTrash2 className='mr-2 h-4 w-4' />
                  Delete Organization
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Organization</AlertDialogTitle>
                <AlertDialogDescription>
                  This action cannot be undone. This will permanently delete the organization{' '}
                  <strong>{organization.name}</strong> and all associated vaults, memberships, and data.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className='space-y-2 py-4'>
                <p className='text-sm text-muted-foreground'>
                  Please type <span className='font-bold select-none'>{organization.name}</span> to confirm.
                </p>
                <Input
                  value={deleteConfirmName}
                  onChange={(e) => setDeleteConfirmName(e.target.value)}
                  placeholder='Enter organization name'
                  disabled={isDeleting}
                />
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
                  disabled={deleteConfirmName !== organization.name || isDeleting}
                  onClick={handleDelete}>
                  {isDeleting && <Spinner className='mr-2 h-4 w-4' />}
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        <TabsContent value='identity' className='flex-1 min-h-0 overflow-hidden'>
          <div className='relative h-full'>
            {tabLoading && (
              <div className='absolute inset-0 z-10 flex items-center justify-center'>
                <Badge variant='outline' className='gap-2'>
                  <Spinner />
                  Loading
                </Badge>
              </div>
            )}
            <div
              className={`h-full transition-opacity duration-300 ease-out ${tabLoading ? 'opacity-0' : 'opacity-100'}`}>
              <OrganizationIdentityPanel
                name={name}
                slug={slug}
                originalSlug={organization.slug}
                onNameChange={setName}
                onSlugChange={setSlug}
              />
            </div>
          </div>
        </TabsContent>

        {canManageMembers && (
          <TabsContent value='members' className='flex-1 min-h-0 overflow-hidden'>
            <div className='relative h-full'>
              {tabLoading && (
                <div className='absolute inset-0 z-10 flex items-center justify-center'>
                  <Badge variant='outline' className='gap-2'>
                    <Spinner />
                    Loading
                  </Badge>
                </div>
              )}
              <div
                className={`h-full transition-opacity duration-300 ease-out ${
                  tabLoading ? 'opacity-0' : 'opacity-100'
                }`}>
                <OrganizationMembersPanel organizationId={organization.id} onReady={finishLoading} />
              </div>
            </div>
          </TabsContent>
        )}

        <TabsContent value='subscription' className='flex-1 min-h-0 overflow-hidden'>
          <div className='relative h-full'>
            {tabLoading && (
              <div className='absolute inset-0 z-10 flex items-center justify-center'>
                <Badge variant='outline' className='gap-2'>
                  <Spinner />
                  Loading
                </Badge>
              </div>
            )}
            <div
              className={`h-full transition-opacity duration-300 ease-out ${tabLoading ? 'opacity-0' : 'opacity-100'}`}>
              <OrganizationSubscriptionPanel organizationId={organization.id} onReady={finishLoading} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value='roadmap' className='flex-1 min-h-0 overflow-hidden'>
          <div className='relative h-full'>
            {tabLoading && (
              <div className='absolute inset-0 z-10 flex items-center justify-center'>
                <Badge variant='outline' className='gap-2'>
                  <Spinner />
                  Loading
                </Badge>
              </div>
            )}
            <div
              className={`h-full transition-opacity duration-300 ease-out ${tabLoading ? 'opacity-0' : 'opacity-100'}`}>
              <OrganizationRoadmapPanel />
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
